import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getVoucherFromVoucherBundle } from 'helpers/bundles'
import { AppMode } from 'helpers/types'
import {
    CartItemCardDetailDto,
    CartItemVoucherProductDetailDto,
    GetAccommodationItemPresenter,
    GetCartApiResponse,
    GetCartItemPresenter,
} from 'store/api-slice/hooks'
import {
    AccommodationTicket,
    BusTicketWithDetails,
    Card,
    MemoryCapture,
    Ticket,
    Shipping,
    CabanaWithDetails,
    VoucherWithDetails,
    TicketsBundle,
    MarchVoucher,
} from 'store/api-slice/types'
import {
    filterMarchPromoVouchersFromCart,
    filterCityBreakTicketsFromCart,
    filterStandaloneTicketsBundlesFromCart,
    filterTicketsWithBundlesFromCart,
} from './helpers'
import { SpaWithDetails } from 'routes/Upsell/SpaCreator/types/spa'
import { filterSpaFromCart } from 'routes/Upsell/SpaCreator/helpers/cart'

interface SetCartPayload extends GetCartApiResponse {
    appMode: AppMode
    itemsSequence?: {
        tickets?: string[]
        spa?: string[]
        busTickets?: string[]
        cabanas?: string[]
        memoryCapture?: string[]
        accommodation?: string[]
        cards?: string[]
        vouchers?: string[]
    }
    isFetchingLocked?: boolean
}
interface CartState extends GetCartApiResponse {
    items: {
        tickets: Ticket[]
        ticketsBundles: TicketsBundle[]
        ticketsCityBreak: Ticket[]
        spa: SpaWithDetails[]
        cabanas: CabanaWithDetails[]
        busTickets: BusTicketWithDetails[]
        memoryCapture: MemoryCapture | null
        accommodation: AccommodationTicket | null
        accommodationDetails: GetAccommodationItemPresenter[] | null
        cards: Card[]
        vouchers: VoucherWithDetails[]
        marchVouchers: MarchVoucher[]
        voucher: string | null
        shipping: Shipping | null
    }
    isFetchingLocked: boolean
}

const initialState: CartState = {
    items: {
        tickets: [],
        ticketsBundles: [],
        ticketsCityBreak: [],
        spa: [],
        busTickets: [],
        cabanas: [],
        memoryCapture: null,
        accommodation: null,
        accommodationDetails: null,
        cards: [],
        vouchers: [],
        marchVouchers: [],
        voucher: null,
        shipping: null,
    },
    isFetchingLocked: true,
}

const CartSlice = createSlice({
    name: 'Cart',
    initialState,
    reducers: {
        // TODO: refactor of this function: remove mutations, improve/add conditions
        setCart(state, action: PayloadAction<SetCartPayload>) {
            const { cart, appMode, itemsSequence, isFetchingLocked } =
                action.payload

            if (!cart) return

            const {
                cartId,
                totalPrice,
                items,
                youSave,
                payOnSpotPrice,
                payOnlinePrice,
                status,
                promotionCouponCode,
                couponSimulationPrice,
                beforeDiscountPrice,
                payByCardPrice,
                remainingCardValue,
                giftCards,
                promotions,
            } = cart

            if (!state.cart) {
                state.cart = {}
            }

            state.cart.cartId = cartId
            state.cart.totalPrice = totalPrice
            state.cart.youSave = youSave
            state.cart.payOnSpotPrice = payOnSpotPrice
            state.cart.payOnlinePrice = payOnlinePrice
            state.cart.status = status
            state.cart.promotionCouponCode = promotionCouponCode
            state.cart.couponSimulationPrice = couponSimulationPrice
            state.cart.beforeDiscountPrice = beforeDiscountPrice
            state.cart.payByCardPrice = payByCardPrice
            state.cart.remainingCardValue = remainingCardValue
            state.cart.giftCards = giftCards
            state.cart.promotions = promotions

            // TODO: try to divide cart store into specific for each creator

            if (appMode?.includes('creator')) {
                // TICKETS
                state.items.tickets = filterTicketsWithBundlesFromCart(items)
                // STANDALONE TICKETS BUNDLES
                state.items.ticketsBundles =
                    filterStandaloneTicketsBundlesFromCart(items)
                // CITY BREAK TICKETS
                state.items.ticketsCityBreak =
                    filterCityBreakTicketsFromCart(items)

                // MARCH PROMO VOUCHERS
                state.items.marchVouchers =
                    filterMarchPromoVouchersFromCart(items)

                // SPA
                state.items.spa = filterSpaFromCart(items)

                // TRANSPORT
                const busTickets =
                    (items?.filter(
                        ({ productType }) => productType === 'bus_ticket'
                    ) as BusTicketWithDetails[]) || []

                state.items.busTickets = busTickets

                // ACCOMMODATION
                const accommodation = items?.find(
                    ({ productType }) => productType === 'accommodation'
                ) as AccommodationTicket

                state.items.accommodation = accommodation

                // CABANAS
                const cabanas =
                    (items?.filter(
                        ({ productType }) => productType === 'cabana'
                    ) as CabanaWithDetails[]) || []

                state.items.cabanas = cabanas

                // MEMORY CAPTURE - restore when re-implemented
                // const memoryCapture = items?.find(
                //     ({ productType }) => productType === 'memory_capture'
                // ) as MemoryCapture

                // state.items.memoryCapture = memoryCapture
            }

            if (appMode?.includes('unlimited')) {
                // UNLIMITED CARDS
                let cards =
                    (items?.filter(
                        ({ productType }) =>
                            (productType === 'card' ||
                                productType === 'bundle') &&
                            appMode?.includes('unlimited')
                    ) as Card[]) || []

                cards = cards.map((card) => {
                    const {
                        productType,
                        itemsBundle,
                        productId,
                        name: bundleName,
                    } = card
                    const isUnlimitedRenew = appMode === 'unlimited-renew'
                    const isItemBundle = productType === 'bundle'
                    const bundledCard = itemsBundle?.find(
                        ({ productType }) => productType === 'card'
                    )

                    if (isItemBundle && bundledCard) {
                        const {
                            cartItemId,
                            detail,
                            description,
                            name: bundledCardName,
                        } = bundledCard || {}

                        return {
                            ...card,
                            productType: 'card',
                            productId,
                            cartItemId,
                            name: isUnlimitedRenew
                                ? bundleName
                                : bundledCardName,
                            description,
                            detail: {
                                ...(detail as CartItemCardDetailDto),
                            },
                        }
                    }
                    return card
                })

                state.items.cards = cards

                // VOUCHER - UNLIMITED RENEW
                const voucher = items?.find(
                    ({ productType }) => productType === 'voucher'
                ) as GetCartItemPresenter

                state.items.voucher = voucher?.cartItemId || null
            }

            if (appMode === 'vouchers') {
                // VOUCHERS
                const cartVouchers = (
                    (items?.filter(
                        ({ productType }) =>
                            productType === 'voucher' ||
                            productType === 'bundle'
                    ) as VoucherWithDetails[]) || []
                ).map((voucher) => {
                    const { productType, itemsBundle, productId } = voucher
                    const isItemBundle = productType === 'bundle'
                    const bundledVoucher =
                        getVoucherFromVoucherBundle(itemsBundle)

                    if (isItemBundle && bundledVoucher) {
                        const {
                            cartItemId,
                            detail,
                            description,
                            name: bundledCardName,
                        } = bundledVoucher || {}

                        return {
                            ...voucher,
                            productType:
                                'voucher' as GetCartItemPresenter['productType'],
                            productId,
                            cartItemId,
                            name: bundledCardName,
                            description,
                            detail: {
                                ...(detail as CartItemVoucherProductDetailDto),
                            },
                        }
                    }
                    return voucher
                })
                const vouchersSortArray = itemsSequence?.vouchers
                const cartVouchersSortedByRedux = vouchersSortArray
                    ? cartVouchers
                          .slice()
                          .sort(
                              (itemA, itemB) =>
                                  vouchersSortArray.indexOf(
                                      itemA.cartItemId || ''
                                  ) -
                                  vouchersSortArray.indexOf(
                                      itemB.cartItemId || ''
                                  )
                          )
                    : [...cartVouchers]

                state.items.vouchers = cartVouchersSortedByRedux
            }

            // SHIPPING
            const shipping = items?.find(
                ({ productType }) => productType === 'shipping'
            ) as Shipping

            state.items.shipping = shipping

            // ---
            state.isFetchingLocked = isFetchingLocked ?? false
        },
        setAccommodationDetails(
            state,
            action: PayloadAction<GetAccommodationItemPresenter[] | null>
        ) {
            state.items.accommodationDetails = action.payload
        },
        setIsFetchingLocked(state, action: PayloadAction<boolean>) {
            state.isFetchingLocked = action.payload
        },
    },
})

export const { setCart, setAccommodationDetails, setIsFetchingLocked } =
    CartSlice.actions
export default CartSlice.reducer
